import { inArray, useLiveQuery } from '@platform/db/tanstack-db';
import { orderEntryCollection } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/order-entry-collection';
import {
  selectSalesPortalCartItemUUIDs,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { useDeferredValue, useMemo } from 'react';

/**
 * Loader for order entry review table data.
 *
 * - Gets cart item UUIDs from the cart store
 * - Defers UUIDs to avoid blocking input during DB queries
 * - Queries the order entry collection for items matching the cart items
 * - Inserts addedAt from the cart store for sorting
 * - Returns the matching order entry items for the review table
 */
export const useSalesPortalOrderEntryReviewDataLoader = () => {
  const cartItemUUIDsString = useCartStore((state) =>
    selectSalesPortalCartItemUUIDs(state).join(','),
  );

  // Convert the comma-separated string back to an array. We use a string in the
  // selector above for referential stability (arrays create new references on
  // each render), then split here only when the string actually changes.
  const cartVariantPackUUIDs = useMemo(
    () => (cartItemUUIDsString ? cartItemUUIDsString.split(',') : []),
    [cartItemUUIDsString],
  );

  // Defer cart UUIDs to avoid blocking input during DB queries when adding new items
  const deferredCartVariantPackUUIDs = useDeferredValue(cartVariantPackUUIDs);

  const { data: rawTableData } = useLiveQuery(
    (q) =>
      deferredCartVariantPackUUIDs?.length > 0
        ? q
            .from({ orderEntryCollection })
            .where(({ orderEntryCollection }) =>
              inArray(
                orderEntryCollection.variantPackUUID,
                deferredCartVariantPackUUIDs,
              ),
            )
        : null,
    [deferredCartVariantPackUUIDs],
  );

  // Items added this session won't have addedAt in the collection yet,
  // so backfill from the cart store to enable sort-by-added-time.
  const tableData = useMemo(() => {
    if (!rawTableData) {
      return rawTableData;
    }
    const cartItems = useCartStore.getState().savedCarts[0]?.cartItems ?? [];
    const addedAtMap = new Map(
      cartItems.map((cartItem) => [cartItem.variantPackUUID, cartItem.addedAt]),
    );
    const cartIndexMap = new Map(
      cartItems.map((cartItem, index) => [cartItem.variantPackUUID, index]),
    );
    return rawTableData
      .map((collectionItem) => ({
        ...collectionItem,
        addedAt:
          addedAtMap.get(collectionItem.variantPackUUID) ??
          collectionItem.addedAt,
      }))
      .sort(
        (a, b) =>
          (cartIndexMap.get(a.variantPackUUID) ?? cartItems.length) -
          (cartIndexMap.get(b.variantPackUUID) ?? cartItems.length),
      );
  }, [rawTableData]);

  return { tableData };
};
